import React from "react";

/* ================================================================
   Gravity & Forces — 8 animated physics templates
   Gravity, projectile, orbit, tidal, magnetic, EM wave, centripetal, free fall
   Clean diagram style with physics annotations
   ================================================================ */

// ── 1. Gravity Well ──────────────────────────────────────────────────
export const GravityWell: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 600 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Grid lines bending into well */}
      {Array.from({ length: 15 }).map((_, i) => {
        const y = 80 + i * 25;
        const bend = Math.max(0, 1 - Math.abs(y - 280) / 200) * 50;
        return (
          <path key={`h-${i}`} d={`M50,${y} Q300,${y + bend} 550,${y}`} fill="none" stroke="#334155" strokeWidth="1" opacity="0.5" />
        );
      })}
      {Array.from({ length: 11 }).map((_, i) => {
        const x = 50 + i * 50;
        const bend = Math.max(0, 1 - Math.abs(x - 300) / 250) * 50;
        return (
          <path key={`v-${i}`} d={`M${x},80 Q${x},${280 + bend} ${x},450`} fill="none" stroke="#334155" strokeWidth="1" opacity="0.5" />
        );
      })}
      {/* Central mass */}
      <circle cx="300" cy="280" r="25" fill="#6366f1">
        <animate attributeName="r" values="23;27;23" dur="3s" repeatCount="indefinite" />
      </circle>
      <text x="300" y="285" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">M</text>
      {/* Orbiting test particle */}
      <circle cx={300 + 100 * Math.cos(phase * 0.3)} cy={280 + 60 * Math.sin(phase * 0.3)} r="6" fill="#f59e0b" />
      {/* Gravity arrows pointing inward */}
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a, i) => {
        const rad = (a * Math.PI) / 180;
        const sx = 300 + 130 * Math.cos(rad);
        const sy = 280 + 100 * Math.sin(rad);
        const ex = 300 + 80 * Math.cos(rad);
        const ey = 280 + 60 * Math.sin(rad);
        return (
          <line key={i} x1={sx} y1={sy} x2={ex} y2={ey} stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrowRed)" opacity="0.5" />
        );
      })}
      <defs>
        <marker id="arrowRed" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#ef4444" />
        </marker>
      </defs>
      <text x="300" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Gravitational Well</text>
    </svg>
  );
};

// ── 2. Projectile Arc ────────────────────────────────────────────────
export const ProjectileArc: React.FC<Record<string, unknown>> = (props) => {
  const flowProgress = (props.flowProgress as number) ?? 0;
  const t = flowProgress;
  const g = 9.8;
  const v0 = 50;
  const angle = 45;
  const rad = (angle * Math.PI) / 180;
  const scale = 5;
  const bx = 100 + v0 * Math.cos(rad) * t * scale;
  const by = 380 - (v0 * Math.sin(rad) * t * scale - 0.5 * g * t * t * scale * scale * 0.02);
  const pathD = Array.from({ length: 50 }).map((_, i) => {
    const ti = i / 50;
    const px = 100 + v0 * Math.cos(rad) * ti * scale;
    const py = 380 - (v0 * Math.sin(rad) * ti * scale - 0.5 * g * ti * ti * scale * scale * 0.02);
    return `${i === 0 ? "M" : "L"}${px},${py}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 800 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Axes */}
      <line x1="80" y1="390" x2="750" y2="390" stroke="#475569" strokeWidth="2" />
      <line x1="80" y1="390" x2="80" y2="40" stroke="#475569" strokeWidth="2" />
      <text x="750" y="410" fill="#94a3b8" fontSize="11">Distance (m)</text>
      <text x="50" y="40" fill="#94a3b8" fontSize="11">Height</text>
      {/* Grid */}
      {[100, 200, 300].map((y) => (
        <line key={y} x1="80" y1={y} x2="750" y2={y} stroke="#1e293b" strokeWidth="1" opacity="0.3" />
      ))}
      {/* Trajectory path */}
      <path d={pathD} fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="4 2" />
      {/* Ball */}
      <circle cx={Math.min(bx, 700)} cy={Math.min(Math.max(by, 50), 385)} r="8" fill="#ef4444">
        <animate attributeName="r" values="7;9;7" dur="0.5s" repeatCount="indefinite" />
      </circle>
      {/* Velocity vector at launch */}
      <line x1="100" y1="380" x2={100 + 40 * Math.cos(rad)} y2={380 - 40 * Math.sin(rad)} stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowGreen)" />
      <text x="140" y="340" fill="#22c55e" fontSize="11">v₀ = {v0} m/s</text>
      <text x="130" y="355" fill="#22c55e" fontSize="10">θ = {angle}°</text>
      <defs>
        <marker id="arrowGreen" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#22c55e" />
        </marker>
      </defs>
      <text x="400" y="435" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Projectile Motion</text>
    </svg>
  );
};

// ── 3. Orbital Mechanics ─────────────────────────────────────────────
export const OrbitalMechanics: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 600 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <rect x="0" y="0" width="600" height="500" fill="#0f172a" rx="12" />
      {/* Sun */}
      <circle cx="300" cy="250" r="30" fill="#fbbf24" />
      <circle cx="300" cy="250" r="35" fill="none" stroke="#fbbf24" strokeWidth="1" opacity="0.3">
        <animate attributeName="r" values="33;38;33" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* Orbits */}
      <ellipse cx="300" cy="250" rx="90" ry="70" fill="none" stroke="#334155" strokeWidth="1" strokeDasharray="3 3" />
      <ellipse cx="300" cy="250" rx="150" ry="110" fill="none" stroke="#334155" strokeWidth="1" strokeDasharray="3 3" />
      <ellipse cx="300" cy="250" rx="220" ry="160" fill="none" stroke="#334155" strokeWidth="1" strokeDasharray="3 3" />
      {/* Planet 1 - fast */}
      <circle cx={300 + 90 * Math.cos(phase * 0.6)} cy={250 + 70 * Math.sin(phase * 0.6)} r="8" fill="#6366f1" />
      {/* Planet 2 - medium */}
      <circle cx={300 + 150 * Math.cos(phase * 0.3)} cy={250 + 110 * Math.sin(phase * 0.3)} r="12" fill="#22c55e" />
      {/* Planet 3 - slow with moon */}
      <g>
        <circle cx={300 + 220 * Math.cos(phase * 0.15)} cy={250 + 160 * Math.sin(phase * 0.15)} r="15" fill="#ef4444" />
        <circle cx={300 + 220 * Math.cos(phase * 0.15) + 25 * Math.cos(phase * 0.8)} cy={250 + 160 * Math.sin(phase * 0.15) + 25 * Math.sin(phase * 0.8)} r="4" fill="#cbd5e1" />
      </g>
      {/* Kepler's labels */}
      <text x="300" y="470" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Orbital Mechanics</text>
      <text x="300" y="30" textAnchor="middle" fill="#94a3b8" fontSize="10">Kepler&apos;s Laws — elliptical orbits</text>
    </svg>
  );
};

// ── 4. Tidal Forces ──────────────────────────────────────────────────
export const TidalForces: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const bulge = 1 + Math.sin(phase * 0.4) * 0.15;
  const moonAngle = phase * 0.2;
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <rect x="0" y="0" width="700" height="400" fill="#0f172a" rx="12" />
      {/* Earth */}
      <circle cx="350" cy="200" r="60" fill="#3b82f6" />
      {/* Tidal bulge - ocean layer */}
      <ellipse cx="350" cy="200" rx={60 * bulge} ry={60 / bulge} fill="#60a5fa" opacity="0.5" />
      {/* Continents */}
      <ellipse cx="340" cy="185" rx="15" ry="20" fill="#22c55e" />
      <ellipse cx="370" cy="210" rx="12" ry="10" fill="#22c55e" />
      {/* Moon */}
      <circle cx={350 + 150 * Math.cos(moonAngle)} cy={200 + 150 * Math.sin(moonAngle)} r="18" fill="#cbd5e1" />
      <circle cx={350 + 150 * Math.cos(moonAngle) - 3} cy={200 + 150 * Math.sin(moonAngle) - 2} r="3" fill="#94a3b8" />
      {/* Force arrows */}
      <line x1={350 + 70 * Math.cos(moonAngle)} y1={200 + 70 * Math.sin(moonAngle)}
            x2={350 + 110 * Math.cos(moonAngle)} y2={200 + 110 * Math.sin(moonAngle)}
            stroke="#f59e0b" strokeWidth="2" markerEnd="url(#arrowYellow)" />
      <defs>
        <marker id="arrowYellow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#f59e0b" />
        </marker>
      </defs>
      <text x="350" y="370" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Tidal Forces</text>
      <text x="350" y="388" textAnchor="middle" fill="#94a3b8" fontSize="10">Moon&apos;s gravity stretches Earth&apos;s oceans</text>
    </svg>
  );
};

// ── 5. Magnetic Field Lines ──────────────────────────────────────────
export const MagneticFieldLines: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Magnet bar */}
      <rect x="230" y="175" width="110" height="50" rx="5" fill="#ef4444" />
      <rect x="340" y="175" width="110" height="50" rx="5" fill="#3b82f6" />
      <text x="285" y="205" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="bold">N</text>
      <text x="395" y="205" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="bold">S</text>
      {/* Field lines curving from N to S */}
      {[30, 60, 90, 120].map((spread, i) => (
        <g key={i}>
          <path d={`M450,200 Q${550 + spread},${200 - spread * 1.5} 350,${200 - spread * 2} Q${130 - spread},${200 - spread * 1.5} 230,200`}
            fill="none" stroke="#22c55e" strokeWidth="1.5" opacity="0.5" strokeDasharray="5 3">
            <animate attributeName="stroke-dashoffset" values="0;-16" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
          </path>
          <path d={`M450,200 Q${550 + spread},${200 + spread * 1.5} 350,${200 + spread * 2} Q${130 - spread},${200 + spread * 1.5} 230,200`}
            fill="none" stroke="#22c55e" strokeWidth="1.5" opacity="0.5" strokeDasharray="5 3">
            <animate attributeName="stroke-dashoffset" values="0;-16" dur={`${2 + i * 0.5}s`} repeatCount="indefinite" />
          </path>
        </g>
      ))}
      {/* Compass needle */}
      <g transform={`rotate(${Math.sin(phase * 0.3) * 15},350,350)`}>
        <circle cx="350" cy="350" r="15" fill="none" stroke="#475569" strokeWidth="2" />
        <polygon points="350,337 345,350 350,355 355,350" fill="#ef4444" />
        <polygon points="350,363 345,350 350,345 355,350" fill="#e0e7ff" />
      </g>
      <text x="350" y="35" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Magnetic Field Lines</text>
    </svg>
  );
};

// ── 6. Electromagnetic Wave ──────────────────────────────────────────
export const ElectromagneticWave: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const waveOffset = phase * 0.5;
  const points = 100;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Axis */}
      <line x1="50" y1="200" x2="750" y2="200" stroke="#475569" strokeWidth="1.5" />
      <text x="760" y="205" fill="#94a3b8" fontSize="10">x</text>
      {/* Electric field (E) - vertical oscillation */}
      <path
        d={Array.from({ length: points }).map((_, i) => {
          const x = 50 + i * 7;
          const y = 200 - 80 * Math.sin((i / 15) * Math.PI * 2 + waveOffset);
          return `${i === 0 ? "M" : "L"}${x},${y}`;
        }).join(" ")}
        fill="none" stroke="#ef4444" strokeWidth="2"
      />
      {/* Magnetic field (B) - depth illusion via opacity */}
      <path
        d={Array.from({ length: points }).map((_, i) => {
          const x = 50 + i * 7;
          const y = 200 - 60 * Math.cos((i / 15) * Math.PI * 2 + waveOffset);
          return `${i === 0 ? "M" : "L"}${x},${y}`;
        }).join(" ")}
        fill="none" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 2"
      />
      {/* Labels */}
      <text x="55" y="100" fill="#ef4444" fontSize="12" fontWeight="bold">E field</text>
      <text x="55" y="125" fill="#3b82f6" fontSize="12" fontWeight="bold">B field</text>
      <text x="400" y="370" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Electromagnetic Wave</text>
      <text x="400" y="388" textAnchor="middle" fill="#94a3b8" fontSize="10">E ⊥ B — propagating at c</text>
    </svg>
  );
};

// ── 7. Centripetal Force ─────────────────────────────────────────────
export const CentripetalForce: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const angle = phase * 0.4;
  const radius = 120;
  const cx = 350;
  const cy = 220;
  const bx = cx + radius * Math.cos(angle);
  const by = cy + radius * Math.sin(angle);
  return (
    <svg viewBox="0 0 700 450" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Circle path */}
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="6 4" />
      {/* Center point */}
      <circle cx={cx} cy={cy} r="6" fill="#6366f1" />
      <text x={cx} y={cy + 20} textAnchor="middle" fill="#94a3b8" fontSize="10">Center</text>
      {/* Object */}
      <circle cx={bx} cy={by} r="12" fill="#f59e0b" />
      {/* Force arrow pointing inward */}
      <line x1={bx} y1={by} x2={cx + (radius - 40) * Math.cos(angle)} y2={cy + (radius - 40) * Math.sin(angle)}
            stroke="#ef4444" strokeWidth="2.5" markerEnd="url(#arrowRed2)" />
      {/* Velocity arrow tangent */}
      <line x1={bx} y1={by} x2={bx - 40 * Math.sin(angle)} y2={by + 40 * Math.cos(angle)}
            stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowGreen2)" />
      <defs>
        <marker id="arrowRed2" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="#ef4444" />
        </marker>
        <marker id="arrowGreen2" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill="#22c55e" />
        </marker>
      </defs>
      {/* Labels */}
      <rect x="50" y="380" width="15" height="15" rx="2" fill="#ef4444" />
      <text x="70" y="392" fill="#e0e7ff" fontSize="11">Centripetal Force (Fc)</text>
      <rect x="280" y="380" width="15" height="15" rx="2" fill="#22c55e" />
      <text x="300" y="392" fill="#e0e7ff" fontSize="11">Velocity (v)</text>
      <text x="350" y="430" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Centripetal Force</text>
    </svg>
  );
};

// ── 8. Free Fall ─────────────────────────────────────────────────────
export const FreeFall: React.FC<Record<string, unknown>> = (props) => {
  const flowProgress = (props.flowProgress as number) ?? 0;
  const t = flowProgress;
  const g = 9.8;
  const height = 350;
  const ballY = 60 + Math.min(t * t * g * 50, height);
  const velocity = t * g * 10;
  // Trail positions
  const trails = Array.from({ length: 6 }).map((_, i) => {
    const tt = Math.max(0, t - i * 0.05);
    return 60 + Math.min(tt * tt * g * 50, height);
  });
  return (
    <svg viewBox="0 0 600 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Height ruler */}
      <line x1="100" y1="60" x2="100" y2="420" stroke="#475569" strokeWidth="2" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <g key={i}>
          <line x1="90" y1={60 + i * 72} x2="110" y2={60 + i * 72} stroke="#475569" strokeWidth="1.5" />
          <text x="75" y={65 + i * 72} textAnchor="middle" fill="#94a3b8" fontSize="9">{(5 - i) * 10}m</text>
        </g>
      ))}
      {/* Ground */}
      <rect x="130" y="415" width="400" height="15" rx="3" fill="#78350f" />
      {/* Trail */}
      {trails.map((ty, i) => (
        <circle key={i} cx="300" cy={ty} r={8 - i} fill="#6366f1" opacity={0.3 - i * 0.04} />
      ))}
      {/* Ball */}
      <circle cx="300" cy={Math.min(ballY, 405)} r="15" fill="#ef4444" stroke="#dc2626" strokeWidth="2" />
      {/* Velocity vector */}
      <line x1="330" y1={Math.min(ballY, 405)} x2="330" y2={Math.min(ballY + velocity * 2, 405)} stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowG3)" />
      <defs>
        <marker id="arrowG3" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#22c55e" />
        </marker>
      </defs>
      {/* Info */}
      <text x="420" y="100" fill="#94a3b8" fontSize="11">g = 9.8 m/s²</text>
      <text x="420" y="120" fill="#94a3b8" fontSize="11">v = {velocity.toFixed(1)} m/s</text>
      <text x="420" y="140" fill="#94a3b8" fontSize="11">t = {(t * 3).toFixed(1)} s</text>
      <text x="300" y="470" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Free Fall</text>
    </svg>
  );
};
